import React, { useState } from 'react';
import { CheckCircle2, Loader2, TrendingUp, ShieldCheck, Zap, Award, FileText } from 'lucide-react';
import { TurnstileWidget } from './security/TurnstileWidget';
import { saveInvestorInquiry, trackEvent } from '../lib/firebase';

interface InvestorSectionProps {
  investorRef: React.RefObject<HTMLDivElement | null>;
}

export const InvestorSection: React.FC<InvestorSectionProps> = ({ investorRef }) => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  const handleInvestorSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const investorType = (formData.get('investorType') as string) || 'Institutional VC';
    const quantum = (formData.get('investmentQuantum') as string) || '';
    
    const data = {
      name: (formData.get('investorName') as string) || '',
      email: (formData.get('investorEmail') as string) || '',
      organization: (formData.get('investorOrg') as string) || '',
      investmentQuantum: `${investorType} | ${quantum}`,
      message: (formData.get('investorMessage') as string) || '',
      turnstileToken: turnstileToken,
      formType: 'investor'
    };

    trackEvent('investor_deck_request', { 
      investor_type: investorType,
      quantum: quantum 
    });

    try {
      // 1. Persist to Firebase Firestore
      await saveInvestorInquiry({
        name: data.name,
        email: data.email,
        organization: data.organization,
        investmentQuantum: data.investmentQuantum,
        message: data.message,
        turnstileToken: data.turnstileToken,
      });

      // 2. Transmit to Cloudflare Edge API (relative or api.uvpulser.com)
      try {
        const endpoint = window.location.hostname.includes('uvpulser') ? '/contact' : 'https://api.uvpulser.com/contact';
        await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Turnstile-Token': turnstileToken
          },
          body: JSON.stringify(data)
        });
      } catch (workerErr) {
        console.info('Worker notice: lead registered in cloud database.');
      }

      setFormSuccess(true);
      trackEvent('investor_inquiry_success');
    } catch (err: any) {
      setFormError('Unable to send inquiry. Please check connection or email juan@afoundry.ai.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <section ref={investorRef} id="investor" className="section-pinned bg-[#0B0C0F] z-[95] text-white">
      {/* Background Ambient Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-[30%] -translate-x-1/2 -translate-y-1/2 w-[55vw] h-[55vh] bg-[#06B6D4]/15 rounded-full blur-[110px]" />
      </div>
      
      <div className="absolute inset-0 flex items-center justify-center pt-16 md:pt-20">
        {/* Left Content - Investor Memorandum */}
        <div className="investor-left-content absolute left-[5vw] top-[12vh] w-[46vw] h-[76vh] flex flex-col justify-between overflow-y-auto pr-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[11px] font-mono uppercase tracking-widest text-[#06B6D4] px-2.5 py-1 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3" /> SEED ROUND OPEN ($2.5M)
              </span>
              <span className="text-xs text-white/50 font-mono">an aFoundry Deep-Tech Venture</span>
            </div>

            <h2 className="text-3xl md:text-5xl font-light mb-4 tracking-tight leading-tight">
              The solid-state disruption of water disinfection.
            </h2>
            <p className="text-white/80 text-sm leading-relaxed mb-6">
              Mercury lamps are facing a mandatory global phase-out under the Minamata Convention. UV Pulser delivers 5× peak germicidal fluence using solid-state pulsed discharge—at 40% lower operating energy.
            </p>

            {/* Core Investment Metrics */}
            <div className="grid grid-cols-2 gap-3.5 mb-6">
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="text-2xl md:text-3xl font-light text-[#06B6D4] mb-0.5">$6.8B</p>
                <p className="text-[11px] text-white/70 uppercase tracking-wider font-mono">TAM by 2030 (14.2% CAGR)</p>
              </div>
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="text-2xl md:text-3xl font-light text-[#06B6D4] mb-0.5">12 Patents</p>
                <p className="text-[11px] text-white/70 uppercase tracking-wider font-mono">GaN Topology & LTCC Packaging</p>
              </div>
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="text-2xl md:text-3xl font-light text-[#06B6D4] mb-0.5">100%</p>
                <p className="text-[11px] text-white/70 uppercase tracking-wider font-mono">Mercury-Free & RoHS Compliant</p>
              </div>
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="text-2xl md:text-3xl font-light text-[#06B6D4] mb-0.5">10⁹+ Pulses</p>
                <p className="text-[11px] text-white/70 uppercase tracking-wider font-mono">Lamp Core Lifespan (5–7 Yrs)</p>
              </div>
            </div>

            {/* Investor Moat Highlights */}
            <div className="space-y-2 text-xs text-white/75 bg-white/5 p-3.5 rounded-xl border border-white/10">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span><strong>Regulatory Tailwind:</strong> UN Minamata Convention phasing out mercury UV lamps worldwide.</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span><strong>Energy Arbitrage:</strong> Instant pulsed on/off replaces 24/7 continuous idle consumption.</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span><strong>Scalable Architecture:</strong> Modular drop-in replacement for residential, commercial & municipal systems.</span>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs text-white/50 font-mono">
            <span>Syndication Lead: aFoundry.ai</span>
            <span>IR: juan@afoundry.ai</span>
          </div>
        </div>
        
        {/* Right Form - Institutional Data Room Request */}
        <div className="investor-right-form absolute left-[54vw] top-[12vh] w-[41vw] h-[76vh]">
          <div className="uv-card bg-white text-slate-900 h-full p-7 flex flex-col justify-between overflow-y-auto shadow-2xl border border-slate-200">
            <div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-2xl font-medium text-slate-900">Investor Portal</h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-100 text-cyan-800 font-semibold uppercase">
                  CONFIDENTIAL
                </span>
              </div>
              <p className="text-[#6B7280] text-xs mb-3">
                Request our Executive Memorandum, Financial Model, and Virtual Data Room access.
              </p>
            </div>
            
            {formSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
                <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mb-4 text-cyan-600">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-medium mb-2 text-slate-900">Pitch Deck Request Received</h4>
                <p className="text-[#6B7280] text-sm max-w-xs mb-4">
                  Thank you. Your request has been securely registered with our syndication team. You will receive the investor overview and data room credentials shortly.
                </p>
                <div className="text-xs text-cyan-800 font-mono bg-cyan-50 p-2 rounded-lg border border-cyan-200">
                  Verification Reference: UVP-INV-{Date.now().toString().slice(-6)}
                </div>
              </div>
            ) : (
              <form onSubmit={handleInvestorSubmit} className="flex-1 flex flex-col space-y-2.5">
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-[11px] font-medium mb-1 text-slate-700">Full Name *</label>
                    <input 
                      type="text" 
                      name="investorName"
                      required
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                      placeholder="e.g. Sarah Lin"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium mb-1 text-slate-700">Corporate Email *</label>
                    <input 
                      type="email" 
                      name="investorEmail"
                      required
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                      placeholder="name@fund.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-[11px] font-medium mb-1 text-slate-700">Firm / Entity *</label>
                    <input 
                      type="text" 
                      name="investorOrg"
                      required
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                      placeholder="VC / Family Office / Corporate"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium mb-1 text-slate-700">Investor Type</label>
                    <select 
                      name="investorType" 
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 bg-white text-slate-800"
                    >
                      <option value="Institutional VC">Institutional VC / Climate Fund</option>
                      <option value="Family Office">Family Office / Private Wealth</option>
                      <option value="Corporate Strategic">Corporate Strategic / Water OEM</option>
                      <option value="Angel Syndicate">Angel Syndicate / Accredited</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-medium mb-1 text-slate-700">Target Allocation / Quantum *</label>
                  <select 
                    name="investmentQuantum"
                    required
                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 bg-white text-slate-800 font-medium"
                  >
                    <option value="">Select ticket size</option>
                    <option value="$50k-$100k">$50,000 – $100,000 (Co-investor)</option>
                    <option value="$100k-$250k">$100,000 – $250,000 (Syndicate Major)</option>
                    <option value="$250k-$500k">$250,000 – $500,000 (Strategic Participant)</option>
                    <option value="$500k-$1m">$500,000 – $1,000,000 (Co-Lead)</option>
                    <option value="$1m+">$1,000,000+ (Lead Investor)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium mb-1 text-slate-700">Areas of Evaluation / Specific Interests</label>
                  <textarea 
                    name="investorMessage"
                    rows={2}
                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 resize-none"
                    placeholder="e.g., Requesting confidential pitch deck, financial model, cap table overview..."
                  />
                </div>

                {/* Cloudflare Turnstile Verification */}
                <TurnstileWidget onVerify={(token) => setTurnstileToken(token)} />

                {formError && (
                  <p className="text-red-500 text-xs">{formError}</p>
                )}

                <button 
                  type="submit" 
                  disabled={formSubmitting}
                  className="uv-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 text-xs py-2.5 font-medium shadow-md hover:shadow-lg transition-shadow"
                >
                  {formSubmitting ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying Security & Transmitting...</>
                  ) : (
                    <><FileText className="w-3.5 h-3.5" /> Request Confidential Deck & Data Room</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
